import type { AccountInfo, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { isInteractionInProgressError } from './needsInteraction';

/**
 * Bug #24685 — every top-level auth navigation the admin portal starts goes through this module.
 *
 * <b>Why a module, not React state.</b> #14687 guarded redirects with msal-react's `inProgress`, read
 * from the closure of whichever `useAuth` instance happened to be calling. Two components whose
 * token requests failed in the same tick both saw `'none'` and both started `acquireTokenRedirect`;
 * `handleSessionExpired` could start a `logoutRedirect` alongside. An interrupted redirect leaves
 * MSAL's interaction-in-progress marker in sessionStorage, and the next sign-in then throws
 * `interaction_in_progress` — the "clear storage for both sites" symptom. A module-level flag is
 * set synchronously by the first caller, so every other caller in the page sees it immediately.
 *
 * The flags reset naturally on the navigation that follows (a new page load is a new module).
 */

let redirectInFlight = false;
let signOutInFlight = false;

/**
 * sessionStorage key recording when this tab last started an interactive re-authentication.
 * Survives the redirect round-trip, which module state cannot.
 */
export const REAUTH_STARTED_AT_KEY = 'xcentral.auth.reauthStartedAt';

/**
 * Loop breaker. If the tab is asked to re-authenticate again within this window of the previous
 * re-authentication, the round-trip did not fix the problem (e.g. the API keeps rejecting a fresh
 * token) and another redirect would only start an automatic redirect loop — the burst of
 * /authorize requests Entra's loop protection punishes. The re-auth is suppressed instead.
 */
export const REAUTH_LOOP_WINDOW_MS = 2 * 60 * 1000;

/**
 * msal-browser 5.6.3 keeps its interaction-in-progress marker in sessionStorage under this literal
 * key — `${PREFIX}.${TemporaryCacheKeys.INTERACTION_STATUS_KEY}` in BrowserCacheManager, where
 * PREFIX is "msal" and the key is "interaction.status". MSAL exports no API that clears ONLY this
 * marker: `clearCache()` without an account clears it, but also removes the signed-in account and
 * its tokens, which re-authentication still needs. So the re-auth retry removes this one key and
 * nothing else. Re-verify the key name when upgrading @azure/msal-browser.
 */
export const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

/** Whether this page has already started an auth navigation (re-auth, sign-in or sign-out). */
export function isAuthRedirectInFlight(): boolean {
  return redirectInFlight;
}

/** Outcome of {@link startReauth}. */
export type ReauthOutcome =
  /** This call started the one redirect. */
  | 'started'
  /** Another caller already started a redirect on this page — that navigation is the recovery. */
  | 'alreadyInFlight'
  /** A re-auth already ran within {@link REAUTH_LOOP_WINDOW_MS}; redirecting again would loop. */
  | 'suppressed';

function describe(error: unknown): string {
  if (error !== null && typeof error === 'object') {
    const e = error as { errorCode?: unknown; name?: unknown };
    if (typeof e.errorCode === 'string' && e.errorCode) return e.errorCode;
    if (typeof e.name === 'string' && e.name) return e.name;
  }
  return 'unknown error';
}

function reauthedRecently(now: number): boolean {
  try {
    const raw = window.sessionStorage.getItem(REAUTH_STARTED_AT_KEY);
    if (!raw) return false;
    const startedAt = Number(raw);
    return Number.isFinite(startedAt) && now - startedAt >= 0 && now - startedAt < REAUTH_LOOP_WINDOW_MS;
  } catch {
    // Storage unavailable: no loop record can be read, so none is assumed.
    return false;
  }
}

function recordReauthStart(now: number): void {
  try {
    window.sessionStorage.setItem(REAUTH_STARTED_AT_KEY, String(now));
  } catch {
    // Storage unavailable — the re-auth still proceeds; only the loop breaker is lost.
  }
}

function clearReauthRecord(): void {
  try {
    window.sessionStorage.removeItem(REAUTH_STARTED_AT_KEY);
  } catch {
    // Nothing to do.
  }
}

function removeInteractionStatusMarker(): void {
  try {
    window.sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  } catch {
    // Storage unavailable — the retry below decides.
  }
}

/**
 * Start `start()`; if MSAL refuses with `interaction_in_progress` (a stale marker left by an
 * interrupted redirect), clear the stuck state and try exactly ONCE more. A second failure — of
 * any kind — propagates. Never loops.
 */
async function startWithOneRetry(
  start: () => Promise<void>,
  clearStuck: () => Promise<void> | void,
): Promise<void> {
  try {
    await start();
  } catch (error) {
    if (!isInteractionInProgressError(error)) throw error;
    try {
      await clearStuck();
    } catch {
      // Could not clear — the single retry below will report the real failure.
    }
    await start();
  }
}

/**
 * Single-flight interactive re-authentication for an account whose session has expired.
 *
 * Only the first caller on the page starts `acquireTokenRedirect`; everyone else gets
 * `'alreadyInFlight'` and must not navigate. The request carries the account and a login hint so
 * Entra does not show the account picker, and `redirectStartPage` so the operator returns to the
 * page they were on.
 *
 * Returns synchronously. The redirect promise is caught here — it is never left to reject
 * unhandled (the `timed_out` "failed_to_redirect" rejection MSAL raises when a navigation does not
 * unload the page was one of the unhandled rejections in prod). If the redirect fails to start the
 * flag is released so a later trigger can try again; the loop-breaker record stays, so that later
 * attempt is not an immediate burst.
 */
export function startReauth(
  msal: Pick<IPublicClientApplication, 'acquireTokenRedirect'>,
  account: AccountInfo,
  request: { scopes: string[] },
): ReauthOutcome {
  if (redirectInFlight) return 'alreadyInFlight';
  const now = Date.now();
  if (reauthedRecently(now)) return 'suppressed';

  redirectInFlight = true;
  recordReauthStart(now);

  const loginHint = account.loginHint || account.username || undefined;
  const redirectRequest: RedirectRequest = {
    ...request,
    account,
    ...(loginHint ? { loginHint } : {}),
    redirectStartPage: window.location.href,
  };

  startWithOneRetry(() => msal.acquireTokenRedirect(redirectRequest), removeInteractionStatusMarker).catch(
    (error: unknown) => {
      redirectInFlight = false;
      console.warn('[MSAL] interactive re-authentication could not start:', describe(error));
    },
  );
  return 'started';
}

/**
 * Single-flight interactive sign-in. A second click while the first redirect is starting is a
 * no-op. If MSAL refuses with `interaction_in_progress`, the stuck interaction state is cleared
 * with `clearCache()` — MSAL's supported way to drop it, and safe here because sign-in only runs
 * with no signed-in account — and sign-in is retried once. Never rejects.
 */
export async function signIn(
  msal: Pick<IPublicClientApplication, 'loginRedirect' | 'clearCache'>,
  request: RedirectRequest,
): Promise<void> {
  if (redirectInFlight) return;
  redirectInFlight = true;
  try {
    await startWithOneRetry(
      () => msal.loginRedirect(request),
      () => msal.clearCache(),
    );
  } catch (error) {
    redirectInFlight = false;
    console.warn('[MSAL] sign-in could not start:', describe(error));
  }
}

/**
 * Sign out. Clears ALL local MSAL state first (`clearCache()` with no account removes every MSAL
 * key, the interaction-in-progress marker included — verified against BrowserCacheManager.clear in
 * 5.6.3), because `logoutRedirect` itself refuses to start while that marker is set. Then signs out
 * at Entra for the active account, with its `login_hint` claim as `logoutHint` so Entra does not
 * land on the account picker. Any failure falls back to the landing page. Never rejects.
 */
export async function signOut(
  msal: Pick<IPublicClientApplication, 'logoutRedirect' | 'clearCache'>,
  account: AccountInfo | null,
): Promise<void> {
  if (signOutInFlight) return;
  signOutInFlight = true;
  // Nothing else may start a redirect once sign-out has begun.
  redirectInFlight = true;
  clearReauthRecord();

  try {
    await msal.clearCache();
  } catch {
    // Storage unavailable — logoutRedirect below still clears what it can.
  }

  try {
    await msal.logoutRedirect({
      postLogoutRedirectUri: '/',
      ...(account ? { account } : {}),
      ...(account?.loginHint ? { logoutHint: account.loginHint } : {}),
    });
  } catch (error) {
    console.warn('[MSAL] sign-out redirect failed, returning to the landing page:', describe(error));
    window.location.assign('/');
  }
}

/** Test seam: forget every flag and the loop-breaker record. */
export function resetAuthSessionForTests(): void {
  redirectInFlight = false;
  signOutInFlight = false;
  clearReauthRecord();
}
